import React, { useState, useEffect, useMemo } from 'react';
import { Drawer, DrawerContent, DrawerNavigation } from '@progress/kendo-react-layout';
import { GridDataStateChangeEvent } from '@progress/kendo-react-grid';
import { process } from '@progress/kendo-data-query';
import { FaBell } from 'react-icons/fa';
import NotificationsGrid from '../../components/grids/NotificationsGrid';
import AddNotificationForm from '../../components/FormComps/Notifications/AddNotificationForm';
import { getNotifications, Notification } from '../../data/notificationData';
import { useToast } from '../../contexts/ToastContext';
import { canEditArea } from '../../helpers/areaAccess';
import '../../components/grids/Grids.css';
import './Notifications.css';

interface NotificationsProps {
  allowedRoutes?: string[];
}

const Notifications: React.FC<NotificationsProps> = (props) => {
  // Notifications.Manage or an administrator (see areaAccess.ts). The server enforces the same.
  const canEdit = canEditArea(props.allowedRoutes, 'Notifications');
  const [notificationsData, setNotificationsData] = useState<Notification[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [addNotificationVisible, setAddNotificationVisible] = useState(false);
  const [dataState, setDataState] = useState<any>({
    skip: 0,
    take: 25,
    filter: undefined,
    sort: undefined
  });
  const { showToast } = useToast();

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      const data = await getNotifications();
      // Flatten the data for easier filtering
      const flattenedData = data.map((notification: Notification) => ({
        ...notification,
        recipientName: notification.recipientEmployee ? notification.recipientEmployee.employeeName : 'Group Notification',
        createdByName: notification.employeeCreatedby ? notification.employeeCreatedby.employeeName : '-',
      }));
      setNotificationsData(flattenedData as any);
      setTotalRecords(flattenedData.length);
    } catch (error) {
      console.error('Error loading notifications:', error);
      showToast('Failed to load notifications', 'error');
    }
  };

  const handleDataStateChange = (event: GridDataStateChangeEvent) => {
    setDataState(event.dataState);
  };

  const processedData = useMemo(() => {
    return process(notificationsData, dataState);
  }, [notificationsData, dataState]);

  const handleAddNotification = () => {
    setAddNotificationVisible(true);
    setExpanded(true);
  };

  const handleExpanded = (value: boolean) => {
    setExpanded(value);
    if (!value) {
      setAddNotificationVisible(false);
    }
  };

  const newNotificationHandler = async () => {
    showToast('Notification sent successfully', 'success');
    setExpanded(false);
    setAddNotificationVisible(false);
    await loadNotifications();
  };

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        marginBottom: '24px',
        padding: '20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px',
        border: '1px solid #edebe9'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <FaBell style={{ fontSize: '24px', color: '#0078d4' }} />
          <h2 style={{ 
            fontSize: '1.25rem', 
            fontWeight: '600',
            margin: 0,
            color: '#323130'
          }}>
            Notifications Management
          </h2>
        </div>
        <p style={{
          fontSize: '14px',
          color: '#605e5c',
          margin: 0
        }}>
          {canEdit ? 'Send and manage system notifications' : 'You have view-only access to notifications.'}
        </p>
      </div>

      <Drawer
        expanded={expanded}
        position="end"
        mode="overlay"
        width={1000}
        onOverlayClick={() => handleExpanded(false)}
      >
        <DrawerContent>
          <DrawerNavigation>
            {addNotificationVisible && canEdit && (
              <AddNotificationForm
                onClose={() => handleExpanded(false)}
                onSuccess={newNotificationHandler}
              />
            )}
          </DrawerNavigation>
        </DrawerContent>
      </Drawer>

      <NotificationsGrid
        data={processedData}
        onAddNew={canEdit ? handleAddNotification : undefined}
        onDataStateChange={handleDataStateChange}
      />
    </div>
  );
};

export default Notifications;
